<script setup>
import { ref, watch, computed } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import StepperItem from "./StepperItem.vue";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  steps: {
    type: Array,
    required: true,
  },
  doneCount: {
    type: Number,
    default: 0,
  },
  totalSteps: {
    type: Number,
    default: 0,
  },
  progress: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["close", "step-click"]);

const { t } = useI18n();

const expandedKey = ref(null);
const lastCurrentKey = ref(null);
const panelRef = ref(null);

onClickOutside(
  panelRef,
  () => {
    if (props.open) emit("close");
  },
  { ignore: [".stepper-trigger"] }
);

const currentStepKey = computed(() => {
  const current = props.steps.find((s) => s.status === "current");
  if (current) return current.key;
  const firstWithDetails = props.steps.find((s) => {
    const d = s.details;
    return d && ((d.todo && d.todo.length) || d.warning);
  });
  return firstWithDetails ? firstWithDetails.key : null;
});

watch(
  () => props.open,
  (val) => {
    if (val) {
      expandedKey.value = currentStepKey.value;
      lastCurrentKey.value = currentStepKey.value;
    } else {
      expandedKey.value = null;
      lastCurrentKey.value = null;
    }
  },
  { immediate: true }
);

watch(currentStepKey, (newKey) => {
  if (!props.open) return;
  if (!newKey) return;
  if (newKey === lastCurrentKey.value) return;
  expandedKey.value = newKey;
  lastCurrentKey.value = newKey;
});

function onToggle(step) {
  expandedKey.value = expandedKey.value === step.key ? null : step.key;
}

function onOpenTab(step) {
  emit("step-click", step);
  emit("close");
}

function onClose() {
  emit("close");
}
</script>

<template>
  <transition name="stepper-panel">
    <aside v-if="open" ref="panelRef" class="stepper-panel">
      <div class="stepper-panel__inner">
        <div class="stepper-panel__header">
          <div class="stepper-panel__intro">
            <SvgIcon
              name="sli-info"
              class="stepper-panel__intro-icon"
              style="fill: none"
            />
            <div class="stepper-panel__intro-text">
              {{
                t(
                  "Чтобы отправить заявление нотариусу, выполните все шаги ниже."
                )
              }}
            </div>
          </div>
          <button
            type="button"
            class="stepper-panel__close"
            :aria-label="t('Закрыть')"
            @click="onClose"
          >
            <SvgIcon name="huge-close" />
          </button>
        </div>

        <div class="stepper-panel__progress">
          <div class="stepper-panel__progress-meta">
            <div class="stepper-panel__progress-label">
              {{ t("Прогресс") }}
            </div>
            <div class="stepper-panel__progress-counter">
              {{ doneCount }} / {{ totalSteps }}
            </div>
          </div>
          <div class="stepper-panel__progress-bar">
            <div
              class="stepper-panel__progress-fill"
              :style="{ width: progress + '%' }"
            />
          </div>
        </div>

        <div class="stepper-panel__list">
          <StepperItem
            v-for="(step, index) in steps"
            :key="step.key"
            :step="step"
            :is-last="index === steps.length - 1"
            :expanded="expandedKey === step.key"
            @toggle="onToggle"
            @open-tab="onOpenTab"
          />
        </div>

        <div class="stepper-panel__decoration" aria-hidden="true">
          <svg
            viewBox="0 0 320 160"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.15">
              <path
                d="M40 80 L80 60 L120 80 L80 100 Z"
                stroke="#a78bfa"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M80 60 L80 20 L120 40 L120 80"
                stroke="#a78bfa"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M40 80 L40 40 L80 20"
                stroke="#a78bfa"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M160 110 L200 90 L240 110 L200 130 Z"
                stroke="#c4b5fd"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M200 90 L200 50 L240 70 L240 110"
                stroke="#c4b5fd"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M160 110 L160 70 L200 50"
                stroke="#c4b5fd"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M240 60 L280 40 L320 60 L280 80 Z"
                stroke="#a78bfa"
                stroke-width="1.2"
                fill="none"
              />
            </g>
          </svg>
        </div>
      </div>
    </aside>
  </transition>
</template>

<style scoped>
.stepper-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  bottom: 10px;
  width: 420px;
  padding-right: 10px;
  z-index: 20;
  overflow: visible;
  pointer-events: none;
}

.stepper-panel__inner {
  pointer-events: auto;
  box-shadow: 0 8px 24px hsl(var(--foreground) / 0.12),
    0 2px 6px hsl(var(--foreground) / 0.06);
}

.stepper-panel__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--card));
  overflow: hidden;
}

.stepper-panel__inner > * {
  padding-left: 18px;
  padding-right: 18px;
}

.stepper-panel__inner > .stepper-panel__decoration {
  padding: 0;
}

.stepper-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding-top: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed hsl(var(--border));
  flex-shrink: 0;
}

.stepper-panel__intro {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
}

.stepper-panel__intro-icon {
  width: 14px;
  height: 14px;
  color: #6366f1;
  flex-shrink: 0;
  margin-top: 2px;
}

.stepper-panel__intro-text {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
}

.stepper-panel__close {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.stepper-panel__close:hover {
  background: hsl(var(--muted));
  color: hsl(var(--foreground));
}

.stepper-panel__close :deep(.svg-icon) {
  width: 14px;
  height: 14px;
}

.stepper-panel__progress {
  padding-top: 12px;
  padding-bottom: 14px;
  flex-shrink: 0;
}

.stepper-panel__progress-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.stepper-panel__progress-label {
  font-size: 11px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  letter-spacing: 0.01em;
}

.stepper-panel__progress-counter {
  font-size: 12px;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.stepper-panel__progress-bar {
  height: 4px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.12);
  overflow: hidden;
}

.stepper-panel__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.35);
}

.stepper-panel__list {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 4px;
  padding-bottom: 16px;
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--muted-foreground) / 0.5) transparent;
}

.stepper-panel__list::-webkit-scrollbar {
  width: 6px;
}

.stepper-panel__list::-webkit-scrollbar-track {
  background: transparent;
}

.stepper-panel__list::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground) / 0.4);
  border-radius: 999px;
}

.stepper-panel__list::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--muted-foreground) / 0.7);
}

.stepper-panel__decoration {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 110px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.7;
}

.stepper-panel__decoration svg {
  width: 100%;
  height: 100%;
}

.stepper-panel-enter-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}

.stepper-panel-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 1, 1),
    opacity 0.25s cubic-bezier(0.4, 0, 1, 1);
  will-change: transform, opacity;
}

.stepper-panel-enter-from {
  transform: translateX(-20px);
  opacity: 0;
}

.stepper-panel-enter-to {
  transform: translateX(0);
  opacity: 1;
}

.stepper-panel-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.stepper-panel-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .stepper-panel-enter-active,
  .stepper-panel-leave-active {
    transition: opacity 0.15s ease;
  }
  .stepper-panel-enter-from,
  .stepper-panel-leave-to {
    transform: none;
  }
}

@media (max-width: 1024px) {
  .stepper-panel {
    width: 320px;
  }
}

@media (max-width: 768px) {
  .stepper-panel {
    width: 280px;
  }
}
</style>
